import React, { Fragment } from "react";
import client from "@culturehq/client";
import { classnames, Badge, Grid, Icon, Loader, Modal, PlainButton } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFetch, useFieldsLabels } from "app/hooks";
import CompanyLink from "app/links/CompanyLink";
import { ClassYearLink, ClubLink, DepartmentLink, InterestLink, LocationLink, ProgramLink, ProfileLink, DegreeLink, SkillLink } from "app/links";
import { getFilledIcon, Trans } from "app/utils";

import Typography from "components/common/Typography";
import UserName from "components/common/UserName";

import ChqModal from "components/common/ChqModal";
import styles from "./styles.module.scss";

const entities = {
  company: "building",
  department: "ios-people-outline",
  location: "loc-ol",
  interest: "heart-ol",
  connection: "person-check",
  skill: "hammer",
  class_year: "clock",
  club: "book",
  program: "ios-star-outline",
  degree: "home-outlined"
};

const EntityLink = ({ type, entity }) => {
  switch (type) {
    case "company":
      return <CompanyLink company={entity} />;
    case "department":
      return <DepartmentLink department={entity} />;
    case "location":
      return <LocationLink location={entity} />;
    case "interest":
      return <InterestLink interest={entity} />;
    case "connection":
      return <ProfileLink user={entity} commonality />;
    case "skill":
      return <SkillLink skill={entity} />;
    case "class_year":
      return <ClassYearLink classYear={entity} />;
    case "club":
      return <ClubLink club={entity} />;
    case "program":
      return <ProgramLink program={entity} />;
    case "degree":
      return <DegreeLink degree={entity} />;
    default:
      return "";
  }
};

const CommonEntity = ({ type, entity }) => (
  <Badge primary>
    <Icon icon={entities[type]} />
    <EntityLink type={type} entity={entity} />
  </Badge>
);

const CommonEntities = ({ commonEntities, fetching }) => (
  <Loader loading={fetching}>
    {commonEntities.map(entity => (
      <Fragment key={`${entity.type}-${entity.id}`}>
        <CommonEntity type={entity.type} entity={entity} />
        {" "}
      </Fragment>
    ))}
  </Loader>
);

const makeConnectionString = (connection, labels) => {
  const parts = Object.keys(entities).filter(key => connection[key]).map(key => {
    if (key === "department") {
      return `${connection[key]} ${(connection[key] === 1 ? labels.departments.singular : labels.departments.plural).toLowerCase()}`;
    }
    return `${connection[key]} ${key}${connection[key] === 1 ? "" : "s"}`;
  });

  return parts.join(", ");
};

const getTrigger = (
  commonEntities, connection, fetching, label, showNumberOfCommonalities, labels
) => {
  if (showNumberOfCommonalities) {
    if (commonEntities.length === 0) {
      return "";
    }

    const categories = {};
    commonEntities.forEach(({ type }) => {
      categories[type] = true;
    });

    return onTrigger => (
      <Loader loading={fetching}>
        <PlainButton className={styles.connection} onClick={onTrigger}>
          {Object.keys(categories).map(category => (
            <Icon key={category} className="Commonality-Icon" icon={getFilledIcon(category)} />
          ))}
          <span className="Commonalities-Number"><Trans>{commonEntities.length} commonalities</Trans></span>
        </PlainButton>
      </Loader>
    );
  }

  if ((!connection || connection.connected) && label) {
    return onTrigger => (
      <PlainButton className={styles.connection} onClick={onTrigger}>
        <Trans>{label}</Trans>
      </PlainButton>
    );
  }

  if (!connection) {
    return null;
  }

  const connectionString = makeConnectionString(connection, labels);
  if (connectionString) {
    return onTrigger => (
      <PlainButton onClick={onTrigger} className={classnames("text-left", styles.connection)}>
        <Icon icon="rings" /> {connectionString}
      </PlainButton>
    );
  }

  return null;
};

const UserCommonalities = ({
  connection, label = null, fallback = null, showNumberOfCommonalities = false, user
}) => {
  const { profile } = useProfile();
  const labels = useFieldsLabels();
  const { fetching, json: { commonEntities = [] } } = useFetch(
    () => client.listUserCommonEntities({ userId: user.id }),
    [user]
  );

  const trigger = getTrigger(
    commonEntities, connection, fetching, label, showNumberOfCommonalities, labels
  );

  if (!trigger) {
    return fallback;
  }

  return (
    <ChqModal trigger={trigger}>
      <Modal.Heading>
        <Trans>Commonalities</Trans>
      </Modal.Heading>
      <Modal.Body>
        <Grid>
          <Grid.Item xs={6}>
            <Typography textCenter textLarge>
              <UserName size="medium" user={user} />
            </Typography>
          </Grid.Item>
          <Grid.Item xs={6}>
            <Typography textCenter textLarge>
              <UserName size="medium" user={profile} />
            </Typography>
          </Grid.Item>
        </Grid>
        <br />
        <p>
          <CommonEntities commonEntities={commonEntities} fetching={fetching} user={user} />
        </p>
      </Modal.Body>
    </ChqModal>
  );
};

export default UserCommonalities;
